import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { colors } from '../theme';
import { Product } from '../data/productsData';
import { fadeInUp, slideInLeft, slideInRight } from '../utils/animations';

/**
 * HEURÍSTICAS APLICADAS NO PRODUCT BLOCK:
 *
 * #1 - Visibilidade do status do sistema:
 *   - Animações ao entrar na viewport
 *   - Feedback visual em hover
 *
 * #2 - Correspondência entre sistema e mundo real:
 *   - Layout alternado (esquerda/direita) para ritmo visual
 *
 * #4 - Consistência e padrões:
 *   - Animações consistentes em todos os blocos
 *
 * #5 - Prevenção de erros:
 *   - Fallbacks para vídeos indisponíveis
 *
 * #8 - Design estético e minimalista:
 *   - Animações sutis que não distraem do conteúdo
 */

interface ProductBlockProps {
  product: Product;
  index: number;
}

const ProductBlock: React.FC<ProductBlockProps> = ({ product, index }) => {
  const isEven = index % 2 === 0;

  const isYouTubeVideo = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const isMp4Video = (url: string): boolean => {
    return url.toLowerCase().endsWith('.mp4');
  };

  const renderMedia = () => {
    if (!product.video || product.video.trim() === '') {
      return (
        <Box
          component={motion.div}
          whileHover={{ scale: 1.02 }}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: { xs: '300px', md: '400px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.darkWine,
            borderRadius: '4px',
            border: `2px solid ${colors.agedGold}`,
            transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.beige,
              textAlign: 'center',
              px: 3,
            }}
          >
            Vídeo em construção
          </Typography>
        </Box>
      );
    }

    if (isYouTubeVideo(product.video)) {
      return (
        <Box
          component={motion.div}
          whileHover={{ scale: 1.01 }}
          sx={{
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            overflow: 'hidden',
            borderRadius: '4px',
            border: `2px solid ${colors.agedGold}`,
            transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
            '&:hover': {
              borderColor: colors.beige,
              boxShadow: `0 8px 24px rgba(194, 161, 76, 0.3)`,
            },
          }}
        >
          <iframe
            src={product.video}
            title={`Vídeo do produto ${product.name}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>
      );
    }

    if (isMp4Video(product.video)) {
      return (
        <Box
          component={motion.div}
          whileHover={{ scale: 1.01 }}
          sx={{
            width: '100%',
            borderRadius: '4px',
            border: `2px solid ${colors.agedGold}`,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
            '&:hover': {
              borderColor: colors.beige,
              boxShadow: `0 8px 24px rgba(194, 161, 76, 0.3)`,
            },
          }}
        >
          <video
            controls
            style={{
              width: '100%',
              display: 'block',
            }}
            aria-label={`Vídeo do produto ${product.name}`}
          >
            <source src={product.video} type="video/mp4" />
            Seu navegador não suporta vídeos HTML5.
          </video>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.darkWine,
          borderRadius: '4px',
          border: `2px solid ${colors.agedGold}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: colors.beige,
            textAlign: 'center',
            px: 3,
          }}
        >
          Formato de vídeo não suportado
        </Typography>
      </Box>
    );
  };

  const textContent = (
    <motion.div
      variants={isEven ? slideInLeft : slideInRight}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography
          variant="h3"
          component="h3"
          sx={{
            color: colors.blackMatte,
            mb: 3,
            fontWeight: 700,
          }}
        >
          {product.name}
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {product.specs.map((spec, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                delay: idx * 0.1,
                duration: 0.3,
                ease: [0.43, 0.13, 0.23, 0.96],
              }}
            >
              <Chip
                component={motion.div}
                whileHover={{ scale: 1.08, y: -2 }}
                whileTap={{ scale: 0.95 }}
                label={spec}
                sx={{
                  backgroundColor: colors.blackMatte,
                  color: colors.beige,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  px: 1,
                  border: `1px solid ${colors.agedGold}`,
                  cursor: 'default',
                }}
              />
            </motion.div>
          ))}
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: colors.blackMatte,
            lineHeight: 1.8,
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}
        >
          {product.description}
        </Typography>
      </Box>
    </motion.div>
  );

  const mediaContent = (
    <motion.div
      variants={isEven ? slideInRight : slideInLeft}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      style={{ height: '100%' }}
    >
      <Box sx={{ height: '100%' }}>{renderMedia()}</Box>
    </motion.div>
  );

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        borderBottom: `1px solid ${colors.agedGold}20`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: isEven ? 'row' : 'row-reverse',
          },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, width: '100%' }}>{textContent}</Box>
        <Box sx={{ flex: 1, width: '100%' }}>{mediaContent}</Box>
      </Box>
    </Box>
  );
};

export default ProductBlock;
